import type {ShellAnalytics} from '@wandb/forge-shell-contract';
import React, {createContext, type ReactNode, use} from 'react';

import {type TrackFn} from './makeTrackEvent';
import {getAnalytics} from './shellAnalytics';

/**
 * Which app's analytics a React tree belongs to.
 *
 * `getAnalytics()` in `./shellAnalytics` answers the same question from module
 * state, and that is enough while one entry is mounted. It is not enough here:
 * `frontends/app/vite.config.ts` exposes `./mount-app` and `./mount-aria` from a
 * single Module Federation container, so both entries share one module registry
 * and one `registerShellAnalytics` slot. Mount the wandb app and the ARIA header
 * slice together, which is every wandb route, and whichever registered last
 * decides the `app_name` on both apps' events.
 *
 * A React tree does not have that problem, because each entry calls `createRoot`
 * and renders its own provider. So the tree is the thing that knows, and this is
 * how an event asks.
 *
 * Reaching it needs a call site inside a component or hook, which the ~250 raw
 * `window.analytics` reads and the plain utility functions are not. Those stay
 * on `getAnalytics()` and stay ambiguous under two entries. This is the path for
 * the events that have somewhere to read from.
 */
const AnalyticsContext = createContext<ShellAnalytics | null>(null);

type AnalyticsProviderProps = {
  children: ReactNode;
  /**
   * Optional because a remote may be mounted by a shell built before the
   * analytics handle was part of the contract, and because the standalone app
   * has no shell to get one from. Both fall through to `getAnalytics()`.
   */
  analytics?: ShellAnalytics;
};

export function AnalyticsProvider({
  analytics,
  children,
}: AnalyticsProviderProps) {
  return (
    <AnalyticsContext value={analytics ?? null}>{children}</AnalyticsContext>
  );
}

/**
 * Never null. Without a provider this is `getAnalytics()`, so an un-provided
 * subtree and the standalone build behave exactly as they do today.
 */
export function useAnalytics(): ShellAnalytics {
  return use(AnalyticsContext) ?? getAnalytics();
}

/**
 * Points an event declared with `makeTrackEvent` at the analytics of the tree
 * it is fired from, keeping the same call signature.
 *
 *     const track = useTrackedEvent(trackAgentChatInteraction);
 *     track({action: 'opened', location: 'project'});
 *
 * Stable as long as the sink is: `analyticsForAria` and `analyticsForShell` are
 * module constants in the shell and `analyticsForRemote` memoizes per remote, so
 * this can go in a dependency array.
 */
export function useTrackedEvent<T>(fn: TrackFn<T>): TrackFn<T> {
  const analytics = useAnalytics();
  return fn.via(analytics);
}
